import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Loader2, Crown, Building2, Rocket, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { stripeService, Product } from '../lib/stripe';
import PricingTitle from '../components/PricingTitle';
import { basicPlans, faqPageDetails, premiumPlans, pricingFeatures } from '../constants';

const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [subscription, setSubscription] = useState<{ status?: string; cancel_at_period_end?: boolean } | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // Check if user has an active subscription
  const hasActiveSubscription = subscription && 
    (subscription.status === 'active' || subscription.status === 'active_until_cancelation');

  useEffect(() => {
    async function loadSubscription() {
      if (user && user.id) {
        try {
          const subscriptionData = await stripeService.getUserSubscription(user.id);
          setSubscription(subscriptionData);
        } catch (err) {
          console.error('Failed to fetch subscription:', err);
        } finally {
          setIsLoadingSubscription(false);
        }
      } else {
        // Clear subscription data when user is not logged in
        setSubscription(null);
        setIsLoadingSubscription(false);
      }
    }

    async function loadProducts() {
      try {
        const fetchedProducts = await stripeService.fetchStripePrices();
        setProducts(fetchedProducts);
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setError('Failed to load pricing information. Please try again later.');
      } finally {
        setIsLoadingProducts(false);
      }
    }

    Promise.all([loadSubscription(), loadProducts()]);
  }, [user]);

  // Clear subscription data and error when user changes
  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setError(null);
      setShowTooltip(false);
    }
  }, [user]);

  const handleSubscribe = async (priceId: string, mode: 'payment' | 'subscription') => {
    if (!user) {
      setError('Please sign in to subscribe');
      return;
    }

    if (hasActiveSubscription) {
      setError('You already have an active subscription');
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
      return;
    }

    try {
      setLoading(priceId);
      setError(null);
      
      const { url } = await stripeService.createCheckoutSession(priceId, mode);
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: unknown) {
      console.error('Checkout error:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to start checkout process');
      }
    } finally {
      setLoading(null);
    }
  };

  const formatPrice = (price: number, currency: string, interval?: string) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);

    return interval ? `${formatted}/${interval}` : formatted;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20">
      {/* Header */}
      <div className="bg-gradient-to-br from-electric-blue/20 to-deep-indigo/15 backdrop-blur-sm border-b border-electric-blue/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Link to="/" className="inline-flex items-center text-electric-blue hover:text-deep-indigo transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" aria-label='Back to Home' />
            Back to Home
          </Link>
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-charcoal">Pricing Plans</h1>
              <p className="text-sm sm:text-base text-slate-gray">Free for biotech companies, premium access for CDMOs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
          >
            <p className="text-red-600 text-sm font-medium">{error}</p>
          </motion.div>
        )}

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-charcoal mb-4">
            Free for Biotech, Premium for CDMOs
          </h2>
          <p className="text-lg text-slate-gray max-w-3xl mx-auto">
            Biotech and pharma companies use our platform completely free. CDMOs pay for premium access to receive warm introduction requests from qualified prospects.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {pricingFeatures.map((feature, index) => (
            <PricingTitle key={feature.title} feature={{ ...feature, index }} />
          ))}
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Plan for Biotech/Pharma */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/50"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-lime-green to-soft-teal rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Rocket className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-charcoal mb-2">Biotech & Pharma Access</h3>
              <p className="text-slate-gray mb-4">Complete platform access for companies seeking CDMOs</p>
              <div className="text-3xl font-bold text-charcoal">Free Forever</div>
            </div>

            <ul className="space-y-3 mb-8">
              {basicPlans.map((feature) => (
                <li key={feature} className="flex items-center">
                  <Check className="w-5 h-5 text-lime-green mr-3 flex-shrink-0" />
                  <span className="text-slate-gray">{feature}</span>
                </li>
              ))}
            </ul>

            <Link to={user ? "/matchmaking" : "/startup-signup"}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full px-6 py-3 bg-gradient-success text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
              >
                {user ? "Go to Dashboard" : "Get Started Free"}
              </motion.button>
            </Link>
          </motion.div>

        {/* Premium Plan for CDMOs */}
        {isLoadingProducts ? (
          <div className="bg-gradient-to-br from-electric-blue/10 to-deep-indigo/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl border-2 border-electric-blue/30 flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-electric-blue" />
          </div>
        ) : (
          products.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-electric-blue/10 to-deep-indigo/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl border-2 border-electric-blue/30 relative overflow-hidden"
            >
              {/* Popular Badge */}
              <div className="absolute top-0 right-0 bg-gradient-primary text-white px-4 py-1 text-sm font-semibold rounded-bl-xl">
                For CDMOs
              </div>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-primary rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-charcoal mb-2">CDMO Premium Access</h3>
                <p className="text-slate-gray mb-4">Receive and respond to warm introduction requests</p>
                <div className="text-3xl font-bold text-charcoal">
                  {formatPrice(product.price, product.currency, product.interval)}
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {premiumPlans.map((feature) => (
                  <li key={feature} className="flex items-center">
                    <Check className="w-5 h-5 text-electric-blue mr-3 flex-shrink-0" />
                    <span className="text-slate-gray">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="relative">
                <motion.button
                  whileHover={hasActiveSubscription ? {} : { scale: 1.02 }}
                  whileTap={hasActiveSubscription ? {} : { scale: 0.98 }}
                  onClick={() => handleSubscribe(product.priceId, product.mode)}
                  // disabled={loading === product.priceId || !user || hasActiveSubscription || isLoadingSubscription}
                  className="w-full px-6 py-4 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center"
                >
                  {loading === product.priceId ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : !user ? (
                    'Sign In to Subscribe'
                  ) : isLoadingSubscription ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Checking subscription...
                    </>
                  ) : hasActiveSubscription ? (
                    'Already Subscribed'
                  ) : (
                    'Start Receiving Introductions'
                  )}
                </motion.button>

                {/* Tooltip for active subscription */}
                {hasActiveSubscription && (
                  <div 
                    className="absolute -top-2 -right-2 cursor-pointer"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={() => setShowTooltip(!showTooltip)}
                  >
                    <Info className="w-5 h-5 text-electric-blue" />
                    {showTooltip && (
                      <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-charcoal text-white text-sm rounded-lg shadow-lg z-10">
                        <p>You already have an active subscription. You can manage it in your account settings.</p>
                        <div className="absolute top-full right-3 -mt-1 border-8 border-transparent border-t-charcoal"></div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {user && (
                <p className="text-center text-sm text-slate-gray mt-3">
                  <Link to="/cdmo-application" className="text-electric-blue hover:underline">
                    Apply as a CDMO partner
                  </Link> to get started
                </p>
              )}
            </motion.div>
          ))
        )}
        </div>
        {/* Value Proposition Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-16 bg-gradient-to-br from-lime-green/10 to-soft-teal/10 rounded-2xl p-8 shadow-lg border border-white/50"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-charcoal mb-4">Why This Model Works</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-lime-green rounded-full flex items-center justify-center mr-3 mt-1">
                    <Rocket className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Free for Biotech</h4>
                    <p className="text-sm text-slate-gray">Removes barriers for startups and biotech companies to find manufacturing partners</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-electric-blue rounded-full flex items-center justify-center mr-3 mt-1">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Value for CDMOs</h4>
                    <p className="text-sm text-slate-gray">CDMOs pay only when they receive qualified leads, ensuring ROI on their investment</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/80 rounded-xl p-6">
              <h4 className="font-semibold text-charcoal mb-3">What CDMOs Get</h4>
              <ul className="space-y-2 text-sm text-slate-gray">
                <li>• Pre-qualified introduction requests</li>
                <li>• Detailed project requirements</li>
                <li>• Company background and development stage</li>
                <li>• Direct contact with decision makers</li>
                <li>• No cold outreach needed</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-16 bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/50"
        >
          <h3 className="text-2xl font-bold text-charcoal mb-6 text-center">Frequently Asked Questions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqPageDetails.map((faq, index) => (
              <div key={index} className="space-y-2">
                <h4 className="font-semibold text-charcoal">{faq.question}</h4>
                <p className="text-sm text-slate-gray">{faq.answer}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
